import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Popover, Box, Typography, Button, List, ListItemButton, Divider,
  CircularProgress, Tooltip
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import api from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import TicketUpdatesDialog from './TicketUpdatesDialog';

/**
 * The signed-in person's notifications (7 Oct 2026), shared by the client, portal,
 * therapist and clinical supervisor headers. Reads /api/inbox, which only ever returns
 * the caller's own items, and updates live over the socket (notification:new and
 * notification:count go to the user's own room).
 *
 * Not a crisis surface: the server refuses critical notices, which keep their own
 * banners and acknowledgement paths.
 *
 * First producers: replies to, and resolution of, a support ticket the person reported.
 * Clicking one opens that ticket's replies in place.
 */

const POLL_MS = 120000; // fallback when the socket is down

const timeAgo = (d) => {
  if (!d || Number.isNaN(new Date(d).getTime())) return 'just now'; // a live refresh carries no date
  const mins = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

const storedToken = () => {
  try { return localStorage.getItem('token'); } catch { return null; }
};

const NotificationBell = ({ token: tokenProp, color = 'inherit', sx }) => {
  // Headers that are not handed the token read it where the app stores it.
  const token = tokenProp || storedToken();
  const socket = useSocket();
  const navigate = useNavigate();
  // A notice with nowhere to go (a client who has left) opens in place so the
  // whole text can be read (ST-20261028-0001).
  const [expandedId, setExpandedId] = useState(null);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticketId, setTicketId] = useState(null);

  const loadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/inbox/count', token);
      setCount(res.data?.needsAction || 0);
    } catch {
      // The badge is a convenience; a failed count leaves the last known value.
    }
  }, [token]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.get('/inbox', token);
      setItems(res.data?.items || []);
      setError('');
    } catch {
      // Never let a failed fetch look like an empty list.
      setError('Could not load notifications. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!socket) return undefined;
    const onCount = (payload) => setCount(payload?.needsAction || 0);
    const onNew = (n) => {
      if (!n?._id) return;
      // A refreshed notice keeps its id: replace it rather than list it twice.
      setItems(prev => [{ ...n, state: 'needs_action' }, ...prev.filter(i => String(i._id) !== String(n._id))]);
    };
    socket.on('notification:count', onCount);
    socket.on('notification:new', onNew);
    // A reconnect may have missed events while the socket was down.
    loadCount();
    return () => {
      socket.off('notification:count', onCount);
      socket.off('notification:new', onNew);
    };
  }, [socket, loadCount]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    loadItems();
  };

  const markSeen = async (item) => {
    if (item.state !== 'needs_action') return;
    setItems(prev => prev.map(i => (i._id === item._id ? { ...i, state: 'seen' } : i)));
    setCount(c => Math.max(0, c - 1));
    try {
      await api.post(`/inbox/${item._id}/seen`, {}, token);
    } catch {
      loadCount();
    }
  };

  const handleItem = (item) => {
    markSeen(item);
    const supportTicketId = item.subject?.supportTicketId;
    if (supportTicketId) {
      setAnchorEl(null);
      setTicketId(typeof supportTicketId === 'object' ? supportTicketId._id : supportTicketId);
      return;
    }
    if (item.link && item.link.startsWith('/')) {
      setAnchorEl(null);
      navigate(item.link);
      return;
    }
    setExpandedId(id => (id === item._id ? null : item._id));
  };

  const markAllSeen = async () => {
    setItems(prev => prev.map(i => (i.state === 'needs_action' ? { ...i, state: 'seen' } : i)));
    setCount(0);
    try {
      await api.post('/inbox/seen-all', {}, token);
    } catch {
      loadCount();
    }
  };

  if (!token) return null;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
          onClick={handleOpen}
          sx={{ color, ...sx }}
        >
          <Badge badgeContent={count} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: '70vh', display: 'flex', flexDirection: 'column' } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
          <Button size="small" onClick={markAllSeen} disabled={!items.some(i => i.state === 'needs_action')}>
            Mark all as read
          </Button>
        </Box>
        <Divider />
        <Box sx={{ overflowY: 'auto' }}>
          {loading && !items.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ px: 2, py: 2 }}>{error}</Typography>
          )}
          {!loading && !error && !items.length && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
              You have no notifications.
            </Typography>
          )}
          {!!items.length && (
            <List disablePadding>
              {items.map(item => {
                const unread = item.state === 'needs_action';
                return (
                  <ListItemButton
                    key={item._id}
                    onClick={() => handleItem(item)}
                    sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{ width: 8, height: 8, mt: 0.9, borderRadius: '50%', flexShrink: 0, bgcolor: unread ? 'primary.main' : 'transparent' }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={unread ? 600 : 400}>
                        {item.title}
                      </Typography>
                      {item.body && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={expandedId === item._id
                            ? { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }
                            : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}
                        >
                          {item.body}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">{timeAgo(item.createdAt)}</Typography>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>

      <TicketUpdatesDialog
        open={Boolean(ticketId)}
        ticketId={ticketId}
        token={token}
        onClose={() => setTicketId(null)}
      />
    </>
  );
};

export default NotificationBell;
